import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subscription, fromEvent, Subject} from 'rxjs';
import {debounceTime, map, shareReplay, switchMap, tap, catchError} from 'rxjs/operators';
import { ToolFixtures } from '../tool.fixtures';
import { DashboardStateService } from '../../dashboard/dashboard-state-service/dashboard-state.service';
import { FileDownloadService } from '../../dashboard/file-download-service/file-download.service';
import { SnackBarService } from '../../core-services/snack-bar/snack-bar.service';
import { UserSettingsApiService } from '../../core/services/api/user-settings-api.service';
import { DashboardApiService } from '../../core/services/api/dashboard-api.service';
import { RoutingService } from '../../core/services/routing.service';
import { MatButton } from '@angular/material/button';
import { AdToolsFilterId, AdToolsFilterOption, AdToolsList, AdToolViewIcon, ToolsDataTableConfig } from '../tool';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { animate, state, style, transition, trigger, keyframes } from '@angular/animations';
import {DashboardPermissionsService} from '../../dashboard/dashboard-permissions-service/dashboard-permissions.service';
import {Router} from '@angular/router';
import { visibilityFadeTrigger } from '../../core/pure-utils/animations';
import { IconCardButtonClickResponse } from '../icon-card/icon-card';

@Component({
    selector: 'app-ad-tools',
    animations: [
        trigger('rotate360', [
            state('true', style({ transform: 'rotate(360deg)' })),
            state('false', style({ transform: 'rotate(0deg)' })),
            transition('true <=> false', animate('500ms ease-in'))
        ]),
        visibilityFadeTrigger,
    ],
    templateUrl: './tool.component.html',
    styleUrls: ['./tool.component.scss'],
    standalone: false
})
export class ToolComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('viewOptionButton') viewOptionButtonRef: MatButton;
  @ViewChild(MatSort, {static: true}) matSort: MatSort;

  constructor(
    private userSettingsApi: UserSettingsApiService,
    private dashboardRoutingService: RoutingService,
    private dashboardStateService: DashboardStateService,
    public dashboardApi: DashboardApiService,
    private fileDownloadService: FileDownloadService,
    private snackBar: SnackBarService,
    private dashboardPermissionService: DashboardPermissionsService,
    private router: Router
  ) { }

  public tools: AdToolsList = {};
  public filters: Array<AdToolsFilterOption>;
  public selectedFilter: AdToolsFilterId = 'all';
  public filteredTools: Array<string> = [];
  public showContent: boolean;
  private favorites: Array<string>;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public Object = Object;

  adToolViewIcon: AdToolViewIcon;
  readonly possibleViewIcons: AdToolViewIcon[] = ['list', 'view_module'];
  savedAdToolViewIcon$Sub = this.dashboardApi.currentUserId$.pipe(
    switchMap(userId => this.userSettingsApi.getAdToolViewIconSetting(userId)),
    map(settingResponse => <AdToolViewIcon> (settingResponse ? settingResponse[0].key_value : 'list'))
  ).subscribe(adToolViewIcon => this.adToolViewIcon = adToolViewIcon);

  viewOptionButtonClick$Sub = new Subscription();

  displayedColumns$ = this.dashboardApi.currentAccount$.pipe(
    map(currentAccount => {
      if (currentAccount.entity_group_name === 'affiliates') {
        return ['toggleSwitch', 'buttons', 'displayName', 'displaySubtitle', 'displayDescription'];
      } else if (currentAccount.entity_group_name !== 'root') {
        return ['buttons', 'displayName', 'displaySubtitle', 'displayDescription'];
      } else {
        return [];
      }
    })
  );

  filteredToolsDataTableSource: MatTableDataSource<ToolsDataTableConfig> = new MatTableDataSource<ToolsDataTableConfig>();
  animateDisplayIcon = false;

  subs = new Subscription();
  usersFavoritedTools$ = this.userSettingsApi.getAdToolFavoritesSetting$.pipe(map(x => x.filter(y => y != null)), shareReplay({bufferSize: 1, refCount: true}));

  ngOnInit() {
    let accountWithPermissions$Sub = this.dashboardPermissionService.accountWithPermissions$.subscribe(([currentAccount, permissions]) => {
      let mappedPermissions = permissions.reduce((acc, singlePermission) => {
        acc[singlePermission.permission_name] = singlePermission.has_permission;
        return acc;
      }, {});

      switch (currentAccount.entity_group_name) {
        // I'm not sure why we're doing it this way.  This is the way the CLB was done, and Andy wanted the same thing
        // Seems like we should just not allow access to the route.
        case 'root': {
          this.showContent = false;
          break;
        }
        case 'affiliates': {
          if (!mappedPermissions['configure_tools']) {
            this.router.navigate(['/access-denied']);
          }
          this.showContent = true;
          break;
        }
        case 'merchants': {
          if (!mappedPermissions['merchant_access']) {
            this.router.navigate(['/access-denied']);
          }
          this.showContent = true;
          break;
        }
        case 'merchant_groups': {
          if (!mappedPermissions['merchant_access']) {
            this.router.navigate(['/access-denied']);
          }
          this.showContent = true;
          break;
        }
      }
    });

    this.subs.add(accountWithPermissions$Sub);

    this.filters = ToolFixtures.getAdToolsFilterOptions();
    this.filteredToolsDataTableSource.sort = this.matSort;
    let currentAccount$Sub = this.dashboardApi.currentAccount$.subscribe((currentAccount) => {
      this.tools = currentAccount.entity_group_name === 'affiliates' ? ToolFixtures.getAffiliateAdToolsList() : ToolFixtures.getMerchantAdToolsList();
      this.filteredTools = [];
      if (currentAccount.entity_group_name === 'affiliates') {
        let usersFavoritedTools$Sub = this.usersFavoritedTools$.subscribe((usersFavoriteTools) => {
          this.favorites = this.favorites || usersFavoriteTools;
          if (this.favorites.length > 0) {
            this.selectedFilter = 'favorite';
            this.tools = this.modifyToolsByFavorites(this.tools, this.favorites);
          }
          this.filteredTools = this.filterTools(this.selectedFilter);
          this.filteredToolsDataTableSource.data = this.filteredTools.map((singleToolId) => ({...this.tools[singleToolId], toolId: singleToolId}));
        });
        this.subs.add(usersFavoritedTools$Sub);
      } else {
        this.selectedFilter = 'all';
        this.filteredTools = this.filterTools(this.selectedFilter);
        this.filteredToolsDataTableSource.data = this.filteredTools.map((singleToolId) => ({...this.tools[singleToolId], toolId: singleToolId}));
      }
    });

    this.subs.add(currentAccount$Sub);
  }

  ngAfterViewInit() {
   let viewOptionButtonClick$Sub = fromEvent(this.viewOptionButtonRef._elementRef.nativeElement, 'click').pipe(
      tap(x => {
        this.adToolViewIcon = this.possibleViewIcons.find(possibleValue => possibleValue !== this.adToolViewIcon);
        this.animateDisplayIcon = !this.animateDisplayIcon;
      }),
      debounceTime(1000),
      switchMap(() => this.dashboardApi.currentAccount$),
      switchMap((currentAccount) => this.userSettingsApi.putAdToolViewIconSetting(currentAccount.user_id, this.adToolViewIcon))
    ).subscribe();
   this.viewOptionButtonClick$Sub.add(viewOptionButtonClick$Sub);
  }

  ngOnDestroy() {
    this.viewOptionButtonClick$Sub.unsubscribe();
    this.subs.unsubscribe();
    this.onFavoriteIconClick$Sub.unsubscribe();
  }

  public filterTools(filterValue: AdToolsFilterId): Array<string> {
    if (filterValue === 'favorite') {
      return this.favorites;
    } else {
      return Object.keys(this.tools).filter((toolKey) => this.tools[toolKey].filterTags[filterValue]);
    }
  }

  private modifyToolsByFavorites(tools: AdToolsList, userFavorites: Array<string>): AdToolsList {
    let returnedTools = JSON.parse(JSON.stringify(tools));
    userFavorites.forEach((favoriteId) => {
      returnedTools[favoriteId].toggleSwitch.state = returnedTools[favoriteId].filterTags['favorite'] = true;
    });
    return returnedTools;
  }

  public handleFilterSelectionChange(filterValue: AdToolsFilterId): void {
    this.filteredTools = this.filterTools(filterValue);
    this.filteredToolsDataTableSource.data = this.filteredTools.map((singleToolId) => ({...this.tools[singleToolId], toolId: singleToolId}));
  }

  public onToolButtonClick(buttonClickResponse: IconCardButtonClickResponse): void {
    if (buttonClickResponse.action === 'route') {
      this.dashboardRoutingService.directToRoute(buttonClickResponse.context.routeKey);
    } else if (buttonClickResponse.action === 'download') {
      this.fileDownloadService.downloadFileFromUrl(buttonClickResponse.context.downloadUrl, buttonClickResponse.context.downloadFileName);
    } else if (buttonClickResponse.action === 'url') {
      this.dashboardRoutingService.routeToUrl(buttonClickResponse.context.routeUrl, true);
    }
  }

  onFavoriteIconClick$ = new Subject<{favorite: boolean; toolId: string}>();
  onFavoriteIconClick$Sub = this.onFavoriteIconClick$.pipe(
    tap(({favorite, toolId}) => {
      if (!favorite) {
        this.favorites = this.favorites.filter((id) => id !== toolId);
      } else if (this.favorites.indexOf(toolId) < 0) {
        this.favorites.push(toolId);
      }

      this.tools = this.modifyToolsByFavorites(ToolFixtures.getAffiliateAdToolsList(), this.favorites);
      this.filteredTools = this.filterTools(this.selectedFilter);
      this.filteredToolsDataTableSource.data = this.filteredTools.map((singleToolId) => ({...this.tools[singleToolId], toolId: singleToolId}));
    }),
    debounceTime(250),
    switchMap(x => this.userSettingsApi.putAdToolFavoritesSetting(this.dashboardStateService.userId, this.favorites.length ? this.favorites : [null])),
    catchError((err, caught) => {
      this.snackBar.openErrorSnackBar(`We were unable to save your changes. Please try again.`);
      return caught;
    })).subscribe(x => this.snackBar.openSnackBar('Your favorites have been saved.'));

  trackByToolId(index: number, item: ToolsDataTableConfig): string {
    return item.toolId;
  }
}
